import { useApiClient } from "@/hooks/useApiClient";
import useSWR, { type SWRResponse } from "swr";
import {
  determineMapServicePermission,
  determineZenrinPermission,
} from "@/utils/determineMapServicePermission";
import { type MapServiceContractResponse } from "../types";

export interface MapServiceContractResult {
  hasGeospaceContract: boolean;
  hasZenrinContract: boolean;
  hasMapViewingPermission: boolean;
  rawContract: MapServiceContractResponse | null;
}

// 5分（ミリ秒）
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * 地図サービス契約情報をSWRで取得し、5分ごとに自動更新するフック
 * @returns 契約情報と権限判定結果
 */
export const useMapServiceContract =
  (): SWRResponse<MapServiceContractResult | null> => {
    const { apiClient } = useApiClient();
    const url = "/user/map-service-contract";

    const fetcher = async (
      fetchUrl: string
    ): Promise<MapServiceContractResult | null> => {
      try {
        const response = await apiClient.get<MapServiceContractResponse>(
          fetchUrl
        );
        const mapServiceContract = response.data;

        // GEOSPACE契約情報を判定
        const hasGeospaceContract = mapServiceContract?.geospace
          ? determineMapServicePermission({
              appId: mapServiceContract.geospace.appId ?? null,
              contractStartDate:
                mapServiceContract.geospace.contractStartDate ?? null,
              contractEndDate:
                mapServiceContract.geospace.contractEndDate ?? null,
              trialStartDate:
                mapServiceContract.geospace.trialStartDate ?? null,
              trialEndDate: mapServiceContract.geospace.trialEndDate ?? null,
            })
          : false;

        // ZENRIN契約情報を判定
        const hasZenrinContract = mapServiceContract?.zenrin
          ? determineZenrinPermission({
              contractStartDate:
                mapServiceContract.zenrin.contractStartDate ?? null,
              contractEndDate:
                mapServiceContract.zenrin.contractEndDate ?? null,
              trialStartDate: mapServiceContract.zenrin.trialStartDate ?? null,
              trialEndDate: mapServiceContract.zenrin.trialEndDate ?? null,
            })
          : false;

        // LocalStorageにも保存（互換性のため）
        if (mapServiceContract?.geospace) {
          localStorage.setItem(
            "mapServiceAppId",
            mapServiceContract.geospace.appId ?? ""
          );
          localStorage.setItem(
            "mapServiceTrialStartDate",
            mapServiceContract.geospace.trialStartDate ?? ""
          );
          localStorage.setItem(
            "mapServiceTrialEndDate",
            mapServiceContract.geospace.trialEndDate ?? ""
          );
          localStorage.setItem(
            "mapServiceContractStartDate",
            mapServiceContract.geospace.contractStartDate ?? ""
          );
          localStorage.setItem(
            "mapServiceContractEndDate",
            mapServiceContract.geospace.contractEndDate ?? ""
          );
        } else {
          localStorage.removeItem("mapServiceAppId");
          localStorage.removeItem("mapServiceTrialStartDate");
          localStorage.removeItem("mapServiceTrialEndDate");
          localStorage.removeItem("mapServiceContractStartDate");
          localStorage.removeItem("mapServiceContractEndDate");
        }

        // ZENRIN契約情報をLocalStorageに保存
        if (mapServiceContract?.zenrin) {
          if (mapServiceContract.zenrin.contractStartDate) {
            localStorage.setItem(
              "mapZenrinContractStartDate",
              mapServiceContract.zenrin.contractStartDate
            );
          } else {
            localStorage.removeItem("mapZenrinContractStartDate");
          }
          if (mapServiceContract.zenrin.contractEndDate) {
            localStorage.setItem(
              "mapZenrinContractEndDate",
              mapServiceContract.zenrin.contractEndDate
            );
          } else {
            localStorage.removeItem("mapZenrinContractEndDate");
          }
          if (mapServiceContract.zenrin.trialStartDate) {
            localStorage.setItem(
              "mapZenrinTrialStartDate",
              mapServiceContract.zenrin.trialStartDate
            );
          } else {
            localStorage.removeItem("mapZenrinTrialStartDate");
          }
          if (mapServiceContract.zenrin.trialEndDate) {
            localStorage.setItem(
              "mapZenrinTrialEndDate",
              mapServiceContract.zenrin.trialEndDate
            );
          } else {
            localStorage.removeItem("mapZenrinTrialEndDate");
          }
        } else {
          localStorage.removeItem("mapZenrinContractStartDate");
          localStorage.removeItem("mapZenrinContractEndDate");
          localStorage.removeItem("mapZenrinTrialStartDate");
          localStorage.removeItem("mapZenrinTrialEndDate");
        }

        return {
          hasGeospaceContract,
          hasZenrinContract,
          hasMapViewingPermission: hasGeospaceContract || hasZenrinContract,
          rawContract: mapServiceContract,
        };
      } catch (error) {
        return null;
      }
    };

    return useSWR(url, fetcher, {
      refreshInterval: REFRESH_INTERVAL_MS,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    });
  };
